import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// All styles
import { makeStyles } from "tss-react/mui";

// Components from MUI library
import AppBar from "@mui/material/AppBar";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";

// Loading related components from MUI library
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

// Common components/controls
import { ButtonUI, PermanentDeletePopup } from "../../Common";
import CommonLayout from "../../Common/Layout";
import { setPrevState } from "../../../util/common";
import { showToast } from "../../../store/actions";
import ManageSchemaClass from "../../../service/manageSchemaService";
import ImportSchemas from "./ImportSchemas";
import HelpContextIcon from "../../Common/HelpPanel/HelpContextIcon";
import SchemaTable from "./SchemaTable";

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
    paddingLeft: 30,
    paddingRight: 30,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  PanelContainer: {
    padding: "10px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "& .MuiAlert-message .MuiList-root": {
      listStyle: "square",
    },
    "& .MuiTableContainer-root": {
      borderRadius: "10px",
    },
  },
  SchemaLabel: {
    color: "black",
    fontWeight: 600,
    fontSize: "0.92rem",
    "& .MuiTypography-root": {
      fontWeight: 600,
    },
  },
  Alert: {
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 20,
  },
}));

const ConfirmPopup = ({ handleDelete, handleCancel, loadingForConfirm, schemasToDelete }) => (
  <PermanentDeletePopup
    item={"Schema"}
    title={
      "Deleting the schema(s) will permanently remove all associated data and may impact your information schemas, data pipelines, queries, saved tables, and any other objects or references dependent on the schema(s). This action is irreversible."
    }
    message={`The following Schema(s) will be permanently deleted:`}
    handleDelete={handleDelete}
    handleCancel={handleCancel}
    loadingForConfirm={loadingForConfirm}
    itemsToDelete={schemasToDelete}
  />
);

const ManageSchema = () => {
  const { classes } = useStyles();
  const scrollRef = useRef(null);
  const { Workspace_id } = useParams();
  const dispatch = useDispatch();
  const workspace = useSelector((state) => state.workspace);
  const { Name: workspaceName, accessRole } = workspace.workspace;

  const [manageSchemaState, setManageSchemaState] = useState({
    loading: false,
    fetchedSchemas: [],
    schemaPermissionDetails: [],
    checkedSchemas: [],
    newSchemas: [],
    error: false,
    edit: false,
    confirmPopup: false,
    loadingDelete: false,
  });

  const [importableSchemas, setImportableSchemas] = useState([]);
  const [importSchemasLoading, setImportSchemasLoading] = useState(false);

  const handleAddSchema = () => {
    const newSchemaid = `S_${Math.random()}`; //NOSONAR
    setPrevState(setManageSchemaState, {
      newSchemas: [...manageSchemaState.newSchemas, { WorkspaceSchema_id: newSchemaid, Name: "", edit: true }],
      schemaPermissionDetails: manageSchemaState.schemaPermissionDetails.map((user) => {
        if (!user.newSchemas) {
          user.newSchemas = {
            [newSchemaid]: {
              Name: "",
              AccessType: 3,
              SchemaType: 3,
            },
          };
        }
        user.newSchemas[newSchemaid] = {
          Name: "",
          AccessType: 3,
          SchemaType: 3,
        };

        return user;
      }),
    });
  };

  const handleSaveClick = async () => {
    setPrevState(setManageSchemaState, { loading: true });

    const submitData = {
      schemaPermissionDetails: manageSchemaState.schemaPermissionDetails,
      newSchemas: manageSchemaState.newSchemas,
      schemas: manageSchemaState.fetchedSchemas,
    };
    const manageSchema = new ManageSchemaClass();
    const { success, message } = await manageSchema.addOrUpdateSchemaDetail(Workspace_id, submitData);

    if (success) {
      const response = await manageSchema.getWorkspaceSchemasWithPermissions(Workspace_id);
      if (response.success) {
        setPrevState(setManageSchemaState, {
          fetchedSchemas: response.data.schemas,
          schemaPermissionDetails: response.data.schemaPermissionDetails.map((data) => ({ ...data, checked: false })),
          newSchemas: [],
          error: false,
          edit: false,
        });
      }
      dispatch(
        showToast({
          message: message,
          type: "success",
        })
      );
    }

    setPrevState(setManageSchemaState, { loading: false });
    getImportableSchemasFromSF();
  };

  const deleteSchemas = async () => {
    setPrevState(setManageSchemaState, { loadingDelete: true });

    const newelyCheckedSchemas = manageSchemaState.checkedSchemas.filter((schema) => schema.startsWith("S_"));

    const manageSchema = new ManageSchemaClass();
    const { success, message } = await manageSchema.deleteSchemas(
      Workspace_id,
      manageSchemaState.checkedSchemas.filter((schema) => !schema.startsWith("S_"))
    );
    if (success) {
      // Filtering out deleted schemas and keeping unsaved schemas
      const newSchemaPermissionDetails = [...manageSchemaState.schemaPermissionDetails];
      let newFetchedSchemas = [...manageSchemaState.fetchedSchemas];
      manageSchemaState.checkedSchemas.forEach((deletedSchemaId) => {
        newSchemaPermissionDetails.forEach((user) => {
          delete user.schemas[deletedSchemaId];
        });

        newFetchedSchemas = newFetchedSchemas.filter(
          ({ WorkspaceSchema_id }) => Number.parseInt(WorkspaceSchema_id) !== Number.parseInt(deletedSchemaId)
        );
      });
      setPrevState(setManageSchemaState, {
        schemaPermissionDetails: newSchemaPermissionDetails,
        fetchedSchemas: newFetchedSchemas,
        newSchemas: manageSchemaState.newSchemas.filter(
          (schema) => !newelyCheckedSchemas.includes(schema.WorkspaceSchema_id)
        ),
      });

      dispatch(
        showToast({
          message: message,
          type: "success",
        })
      );
      setPrevState(setManageSchemaState, { checkedSchemas: [] });
    }
    setPrevState(setManageSchemaState, {
      loadingDelete: false,
      confirmPopup: false,
    });
  };

  const handleDeleteCancel = () => setPrevState(setManageSchemaState, { confirmPopup: false });

  const fetchSchemaPermissionDetails = useCallback(async () => {
    const manageSchema = new ManageSchemaClass();
    const { success, data } = await manageSchema.getWorkspaceSchemasWithPermissions(Workspace_id);
    if (success) {
      setPrevState(setManageSchemaState, {
        fetchedSchemas: data.schemas,
        schemaPermissionDetails: data.schemaPermissionDetails.map((data) => ({ ...data, checked: false })),
      });
    }
  }, [Workspace_id]);

  const getImportableSchemasFromSF = useCallback(async () => {
    setImportSchemasLoading(true);
    const manageSchemaServices = new ManageSchemaClass();
    const { success, data } = await manageSchemaServices.getImportableSchemasFromSF(Workspace_id);
    if (success) {
      setImportableSchemas(data.importSchemas);
    }
    setImportSchemasLoading(false);
  }, [Workspace_id]);

  const handleImportSchemas = useCallback(
    async (checkedSchemas) => {
      setImportSchemasLoading(true);
      let newSchemas = [...manageSchemaState.newSchemas];
      let newSchemaPermissionDetails = [...manageSchemaState.schemaPermissionDetails];

      checkedSchemas.forEach((schema) => {
        const schemaID = `S_${Math.random()}`; //NOSONAR
        newSchemas = [...newSchemas, { WorkspaceSchema_id: schemaID, Name: schema, edit: false }];
        newSchemaPermissionDetails = newSchemaPermissionDetails.map((user) => {
          if (!user.newSchemas) {
            user.newSchemas = {
              [schemaID]: {
                Name: schema,
                AccessType: 3,
                SchemaType: 3,
              },
            };
          }
          user.newSchemas[schemaID] = {
            Name: schema,
            AccessType: 3,
            SchemaType: 3,
          };
          return user;
        });
      });

      setPrevState(setManageSchemaState, {
        schemaPermissionDetails: newSchemaPermissionDetails,
        newSchemas: newSchemas,
      });

      setImportSchemasLoading(false);

      setImportableSchemas((prevState) => [
        ...prevState.filter(({ SCHEMA_NAME }) => !checkedSchemas.includes(SCHEMA_NAME)),
      ]);
    },
    [manageSchemaState.newSchemas, manageSchemaState.schemaPermissionDetails]
  );

  useEffect(() => {
    const asyncFunc = async () => {
      setPrevState(setManageSchemaState, { loading: true });
      await fetchSchemaPermissionDetails();
      setPrevState(setManageSchemaState, { loading: false });

      if (accessRole === 1) await getImportableSchemasFromSF();
    };

    asyncFunc();
  }, [fetchSchemaPermissionDetails, getImportableSchemasFromSF, accessRole]);

  useEffect(() => {
    if (manageSchemaState.newSchemas.length !== 0) {
      scrollRef.current.scrollLeft = scrollRef?.current?.scrollWidth;
    }
  }, [manageSchemaState.newSchemas]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    {
      link: `/my-workspaces/${Workspace_id}`,
      displayName: workspaceName,
      active: false,
    },
    { link: "#", displayName: "Manage Schemas", active: true },
  ];

  const selectedCount = useMemo(
    () => manageSchemaState.schemaPermissionDetails.filter((user) => user.checked).length,
    [manageSchemaState.schemaPermissionDetails]
  );

  //Deleting schemas
  const handleDeleteClick = () => {
    //If all new then directly deleting else show confirm popup
    if (manageSchemaState.checkedSchemas.every((schema) => schema.startsWith("S_"))) {
      setManageSchemaState((prevState) => {
        const _schemaPermissionDetails = [...prevState.schemaPermissionDetails];
        manageSchemaState.checkedSchemas.forEach((deletedSchemaId) => {
          _schemaPermissionDetails.forEach((user) => {
            delete user.schemas[deletedSchemaId];
          });
        });

        return {
          ...prevState,
          newSchemas: prevState.newSchemas.filter(
            (schema) => !prevState.checkedSchemas.includes(schema.WorkspaceSchema_id)
          ),
          schemaPermissionDetails: _schemaPermissionDetails,
        };
      });
    } else
      setPrevState(setManageSchemaState, {
        confirmPopup: true,
      });
  };

  return (
    <CommonLayout title="Settings" breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflowY: "auto",
          paddingBottom: "40px",
        }}
      >
        {manageSchemaState.loading ? (
          <Backdrop
            sx={{
              backgroundColor: "transparent",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={true}
          >
            <CircularProgress color="primary" />
          </Backdrop>
        ) : (
          <>
            <AppBar
              sx={{
                position: "relative",
                boxShadow: 0,
                backgroundColor: "#e8edf1",
              }}
            >
              <Toolbar className={classes.Header}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography className={classes.HeaderTitle} gutterBottom component="div">
                    Manage Schemas
                  </Typography>
                </Box>
                <Box sx={{ display: "inline-flex", flex: 1, alignItems: "center" }}>
                  {[1, 2].includes(accessRole) && (
                    <>
                      <ButtonUI
                        variant="contained"
                        endIcon={<AddIcon />}
                        color="info"
                        style={{ marginRight: "20px" }}
                        onClick={handleAddSchema}
                        disabled={manageSchemaState.error || manageSchemaState.edit}
                      >
                        Add Schema
                      </ButtonUI>
                      <ButtonUI
                        variant="contained"
                        color="success"
                        style={{ marginRight: "10px" }}
                        disabled={manageSchemaState.error || manageSchemaState.edit}
                        onClick={handleSaveClick}
                      >
                        Save
                      </ButtonUI>
                      <IconButton
                        color="error"
                        variant="contained"
                        disabled={manageSchemaState.checkedSchemas.length === 0}
                        onClick={handleDeleteClick}
                      >
                        <DeleteIcon
                          sx={{
                            fontSize: "1.8rem",
                          }}
                        />
                      </IconButton>
                    </>
                  )}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginRight: "auto",
                    }}
                  >
                    <HelpContextIcon screen="Manage Schemas" />
                  </Box>
                  {!!manageSchemaState.edit && (
                    <Box className={classes.HeaderTitle} sx={{ display: "inline-flex", alignItems: "end" }}>
                      <Alert
                        className={classes.Alert}
                        variant="outlined"
                        icon={<WarningIcon fontSize="inherit" />}
                        severity="warning"
                      >
                        You will need to change schema name in jobs and saved queries for this schema manually.
                      </Alert>
                    </Box>
                  )}
                  {!manageSchemaState.edit && !!selectedCount && (
                    <Box className={classes.HeaderTitle} sx={{ display: "inline-flex", alignItems: "end" }}>
                      <Alert
                        className={classes.Alert}
                        variant="outlined"
                        icon={<InfoOutlinedIcon fontSize="inherit" />}
                        severity="info"
                      >
                        '{selectedCount}' user(s) selected for bulk assignment or reassignment of schema permissions.
                      </Alert>
                    </Box>
                  )}
                </Box>
              </Toolbar>
            </AppBar>
            <Box className={classes.PanelContainer}>
              <Paper sx={{ width: "100%", mb: 2, maxHeight: "calc(100vh - 180px)" }}>
                <TableContainer
                  sx={{
                    height: "100%",
                  }}
                  ref={scrollRef}
                >
                  <SchemaTable setManageSchemaState={setManageSchemaState} manageSchemaState={manageSchemaState} />
                </TableContainer>
              </Paper>
            </Box>
            {accessRole === 1 && (
              <ImportSchemas
                schemas={importableSchemas}
                loading={importSchemasLoading}
                handleImportSchemas={handleImportSchemas}
              />
            )}
          </>
        )}
      </Box>
      {manageSchemaState.confirmPopup && (
        <ConfirmPopup
          handleDelete={deleteSchemas}
          handleCancel={handleDeleteCancel}
          loadingForConfirm={manageSchemaState.loadingDelete}
          schemasToDelete={manageSchemaState.checkedSchemas
            .map(
              (checkedSchemaId) =>
                manageSchemaState.fetchedSchemas.find((schema) => String(schema.WorkspaceSchema_id) === checkedSchemaId)
                  ?.Name
            )
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))}
        />
      )}
    </CommonLayout>
  );
};

export default ManageSchema;
